import { useEffect, useState, type FormEvent } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import type { SiteSettings } from "@/types/content";

const FIELDS: {
  key: Exclude<keyof SiteSettings, "site_active">;
  label: string;
  type?: "text" | "textarea";
  hint?: string;
}[] = [
  { key: "brand", label: "Marca" },
  { key: "tagline", label: "Tagline (frase corta que aparece en el header y footer)" },
  { key: "phone_display", label: "Teléfono (formato para mostrar, ej. +56 9 1234 5678)" },
  { key: "phone_raw", label: "Teléfono para WhatsApp (solo números, con código de país, ej. 56912345678)" },
  { key: "email", label: "Email de contacto" },
  { key: "address", label: "Dirección" },
  { key: "instagram", label: "Usuario de Instagram (ej. @espacioborman)" },
  { key: "instagram_url", label: "Link a Instagram" },
  { key: "whatsapp_default_message", label: "Mensaje por defecto al escribir por WhatsApp", type: "textarea" },
  { key: "hours_weekday", label: "Horario lunes a viernes" },
  { key: "hours_saturday", label: "Horario sábado" },
  { key: "hours_units", label: "Horario de acceso a las unidades" },
  { key: "map_query", label: "Ubicación para el mapa (dirección o lugar que se busca en Google Maps)" },
  {
    key: "map_url",
    label: "Link de Google Maps de la ubicación real",
    hint: "En Google Maps busca la bodega, presiona Compartir y copia el link (también sirve el código de «Incorporar un mapa»). Si lo completas, el mapa del sitio muestra ese punto exacto y aparece el botón «Abrir en Google Maps». Si lo dejas vacío, se usa la dirección de arriba.",
  },
  { key: "seo_title", label: "Título SEO (aparece en Google y en la pestaña del navegador)" },
  { key: "seo_description", label: "Descripción SEO", type: "textarea" },
  { key: "seo_keywords", label: "Palabras clave SEO (separadas por coma)", type: "textarea" },
];

export default function AdminSettings() {
  const [values, setValues] = useState<Partial<SiteSettings>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [driveConfigured, setDriveConfigured] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/site-settings`)
      .then((res) => res.json())
      .then((json) => setValues(json))
      .finally(() => setLoading(false));
    fetch(`${API_BASE}/media/storage-status`)
      .then((res) => res.json())
      .then((json) => setDriveConfigured(json.googleDriveConfigured))
      .catch(() => {});
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/site-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setMessage({ type: "error", text: json?.error || "No se pudo guardar" });
        return;
      }
      const { map_warning: mapWarning, ...saved } = json;
      setValues(saved);
      setMessage(
        mapWarning
          ? { type: "error", text: `Cambios guardados. Aviso: ${mapWarning}` }
          : { type: "success", text: "Cambios guardados." }
      );
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Datos del sitio</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Esta información se usa en todo el sitio: header, footer, botón de WhatsApp, y meta datos para Google.
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        {message && (
          <div
            className={`rounded-md p-3 text-sm border ${
              message.type === "success"
                ? "bg-primary-100 border-primary-200 text-primary-800"
                : "bg-accent-100 border-accent-200 text-accent-800"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="rounded-lg border border-background-200 bg-background-100 p-5">
          <h2 className="font-heading font-semibold text-foreground-900">Almacenamiento de imágenes</h2>
          <p className="mt-1 text-xs text-foreground-500">
            Dónde se guardan las imágenes nuevas que subas desde el admin. Las que ya están subidas no se mueven.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <select
              value={values.media_provider ?? "local"}
              onChange={(e) => setValues((v) => ({ ...v, media_provider: e.target.value as SiteSettings["media_provider"] }))}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm"
            >
              <option value="local">VPS (este servidor)</option>
              <option value="google_drive">Google Drive</option>
            </select>
            {values.media_provider === "google_drive" && (
              <span className={`text-xs font-medium ${driveConfigured ? "text-primary-700" : "text-accent-700"}`}>
                {driveConfigured ? "✓ Credenciales de Drive configuradas" : "⚠ Faltan credenciales de Drive en el servidor"}
              </span>
            )}
          </div>
        </div>

        {FIELDS.map((field) => (
          <div key={field.key}>
            <label htmlFor={field.key} className="block text-sm font-medium text-foreground-700 mb-1.5">
              {field.label}
            </label>
            {field.type === "textarea" ? (
              <textarea
                id={field.key}
                rows={3}
                value={values[field.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            ) : (
              <input
                id={field.key}
                type="text"
                value={values[field.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            )}
            {field.hint && <p className="mt-1.5 text-xs text-foreground-500">{field.hint}</p>}
          </div>
        ))}

        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition disabled:opacity-60 cursor-pointer"
        >
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </form>
    </AdminLayout>
  );
}
